import {
  type TicketMessageCitation,
  dedupeGraphCitations,
  dedupeRuleCitations,
  isGraphCitation,
  isKnowledgeRuleCitation,
} from "./commentSourceUtils";

import { CLAIM_NODE } from "../../../utils/graphNodes";

/** The one message whose citations the Cited claims panel is showing. */
export type FocusedCitedClaims = {
  messageId: string;
  citations: TicketMessageCitation[];
};

/** A message as far as this selection cares: role, whether it has landed, and
 * what it cited. Structural on purpose so the route's fuller message type
 * satisfies it without this module importing the route. */
type CitableMessage = {
  _id: string;
  role?: string;
  content?: unknown;
  status?: string;
  citations?: readonly TicketMessageCitation[];
};

/** Citations from one message, in the order the panel renders them: rule chips
 * first, then the graph relationships behind them.
 *
 * why: the two are different shapes and `dedupeRuleCitations` filters graph
 * citations out. Deriving the panel's input from rules alone silently drops
 * every graph relationship the answer was built on.
 */
export function citationsForPanel(
  message: CitableMessage,
): TicketMessageCitation[] {
  const raw = message.citations ?? [];
  return [...dedupeRuleCitations(raw), ...dedupeGraphCitations(raw)];
}

/** Which message the panel should show.
 *
 * A clicked chip pins its own message (`focusedMessageId`); otherwise the most
 * recent assistant message that actually cited something wins, so the panel
 * tracks the conversation instead of emptying on a turn with no citations.
 * A pending message with no content yet is skipped - its citations arrive with
 * the content, and showing it early empties the panel mid-stream.
 */
export function selectFocusedCitedClaims(
  messages: readonly CitableMessage[],
  focusedMessageId?: string | null,
): FocusedCitedClaims | null {
  const pick = (message: CitableMessage): FocusedCitedClaims | null => {
    if (message.role !== "assistant") return null;
    if (!message.content && message.status === "pending") return null;
    const citations = citationsForPanel(message);
    if (citations.length === 0) return null;
    return { messageId: message._id, citations };
  };

  if (focusedMessageId) {
    const targeted = messages.find((m) => m._id === focusedMessageId);
    if (targeted) {
      const hit = pick(targeted);
      if (hit) return hit;
    }
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const hit = pick(messages[i]);
    if (hit) return hit;
  }
  return null;
}

/** The focused answer's citations without the rules it only matched.
 *
 * why: the legacy chat's panel is titled "Cited claims" and has no related
 * group, so a `related` rule there contradicts the chip row under the answer,
 * which counts it as "+n related".
 *
 * why the graph pass: dropping the CHIP is not enough, because a related rule
 * reaches the panel a second way. warhol draws a `claim:<ruleId>` graph record
 * for every rule a retrieved memory names and runs that over the whole ranked
 * list, related fill rows included (`memory_citations.py::memory_records`,
 * called with `ranked` in `knowledge_citations.py`); the graph tool traverses
 * them too. `CitedClaimsPanel` renders such a record in its "Relationships"
 * block labelled with `graphLabel` — which is the related rule's own
 * statement. So the rule whose chip was just hidden was still listed under
 * "Cited claims", exactly the attribution this filter exists to prevent, and
 * on a turn whose rules were ALL fill it was the only thing the panel showed.
 * Only the claim nodes of the rules dropped HERE go; a cited rule's own node,
 * a neighbouring claim this turn never chipped, and every non-claim node stay.
 */
export function withoutRelatedClaims(
  focused: FocusedCitedClaims | null,
): FocusedCitedClaims | null {
  if (!focused) return null;
  const isRelated = (citation: TicketMessageCitation): boolean =>
    "related" in citation && citation.related === true;
  // Collected in a full first pass: `citationsForPanel` emits every rule chip
  // before any graph record, so the node is seen after the rule that hides it.
  const hiddenNodes = new Set<string>();
  for (const citation of focused.citations) {
    if (isRelated(citation) && isKnowledgeRuleCitation(citation)) {
      hiddenNodes.add(`${CLAIM_NODE}${citation.ruleId}`);
    }
  }
  const citations = focused.citations.filter((citation) => {
    if (isRelated(citation)) return false;
    return !(
      isGraphCitation(citation) && hiddenNodes.has(citation.graphNodeId)
    );
  });
  return citations.length > 0 ? { ...focused, citations } : null;
}
